import React, { useState, useMemo } from 'react';
import { Container, Form, Button, Alert, Card, InputGroup, ProgressBar } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../services/api';
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa';
import { calculatePasswordStrength } from '../../utils/passwordStrength';

// Validation schemas
const accountSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .regex(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid email address (e.g., user@example.com)'
    )
    .transform(val => val.toLowerCase().trim()),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .max(50, 'Password must be less than 50 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  confirmPassword: z.string(),
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes')
    .transform(val => val.trim()),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const createOrgSchema = z.object({
  orgId: z.string()
    .min(2, 'Organization ID must be at least 2 characters')
    .max(20, 'Organization ID must be less than 20 characters')
    .regex(/^[a-z0-9_]+$/, 'Organization ID can only contain lowercase letters, numbers, and underscores'),
});

const joinOrgSchema = z.object({
  orgCode: z.string()
    .length(6, 'Organization code must be exactly 6 characters')
    .regex(/^[A-Z0-9]+$/, 'Organization code must be uppercase letters and numbers only')
    .transform(val => val.toUpperCase().trim()),
  orgId: z.string()
    .min(2, 'Organization ID is required')
    .max(20, 'Organization ID must be less than 20 characters')
    .regex(/^[a-z0-9_]+$/, 'Organization ID can only contain lowercase letters, numbers, and underscores'),
});

type AccountFormData = z.infer<typeof accountSchema>;
type AccountData = Omit<AccountFormData, 'confirmPassword'>;
type CreateOrgFormData = z.infer<typeof createOrgSchema>;
type JoinOrgFormData = z.infer<typeof joinOrgSchema>;

type Step = 'account' | 'org-choice' | 'create-org' | 'join-org';

const generateOrgId = (orgName: string): string => {
  const wordsToExclude = new Set(['the', 'of', 'and', 'an', 'a', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'as']);
  
  // If name is long and has many words, create an acronym
  if (orgName.length > 20) {
    const words = orgName.toLowerCase().split(/[\s_]+/);
    const significantWords = words.filter(word => !wordsToExclude.has(word));
    
    if (significantWords.length > 4) {
      const acronym = significantWords
        .map(word => word.charAt(0))
        .join('')
        .toLowerCase();
      
      // If acronym is too short, fall back to normal generation
      if (acronym.length < 3) {
        return normalIdGeneration(orgName);
      }
      
      return acronym;
    }
  }
  
  return normalIdGeneration(orgName);
};

const normalIdGeneration = (orgName: string): string => {
  return orgName
    .toLowerCase()
    .trim()
    // Remove apostrophes
    .replace(/'/g, '')
    // Replace special characters and spaces with underscores
    .replace(/[^a-z0-9]+/g, '_')
    // Remove leading and trailing underscores
    .replace(/^_+|_+$/g, '')
    // Limit length to 20 characters
    .slice(0, 20);
};

const AccountCreationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>('account');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [organizationName, setOrganizationName] = useState('');

  const accountForm = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
  });

  const createOrgForm = useForm<CreateOrgFormData>({
    resolver: zodResolver(createOrgSchema),
  });

  const joinOrgForm = useForm<JoinOrgFormData>({
    resolver: zodResolver(joinOrgSchema),
  });

  const passwordStrength = useMemo(() => {
    const password = accountForm.watch('password') || '';
    return calculatePasswordStrength(password);
  }, [accountForm.watch('password')]);

  const passwordMatch = useMemo(() => {
    const password = accountForm.watch('password') || '';
    const confirmPassword = accountForm.watch('confirmPassword') || '';
    return {
      matches: password === confirmPassword && password !== '',
      message: password === confirmPassword ? 'Passwords match' : 'Passwords do not match'
    };
  }, [accountForm.watch('password'), accountForm.watch('confirmPassword')]);

  const isAccountFormValid = useMemo(() => {
    const values = accountForm.getValues();
    const errors = accountForm.formState.errors;
    const isDirty = accountForm.formState.isDirty;
    
    return (
      isDirty &&
      values.email &&
      values.password &&
      values.confirmPassword &&
      values.name &&
      Object.keys(errors).length === 0 &&
      passwordMatch.matches
    );
  }, [
    accountForm.formState.isDirty,
    accountForm.formState.errors,
    accountForm.getValues(),
    passwordMatch.matches
  ]);

  // Watch for orgName changes to update orgId
  React.useEffect(() => {
    if (organizationName) {
      const generatedId = generateOrgId(organizationName);
      createOrgForm.setValue('orgId', generatedId, { shouldValidate: true });
    }
  }, [organizationName, createOrgForm]);

  const onAccountSubmit = async (data: AccountFormData) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { confirmPassword, ...accountDataWithoutConfirm } = data;
    setAccountData(accountDataWithoutConfirm);
    setCurrentStep('org-choice');
  };

  const onCreateOrgSubmit = async (data: CreateOrgFormData) => {
    if (!accountData) return;
    
    try {
      setIsLoading(true);
      setError(null);
      await api.post('/customers/self-service/create', {
        ...accountData,
        orgName: organizationName,
        ...data
      });
      window.location.href = '/login';
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  const onJoinOrgSubmit = async (data: JoinOrgFormData) => {
    if (!accountData) return;

    try {
      setIsLoading(true);
      setError(null);
      await api.post('/customers/self-service/join', {
        ...accountData,
        ...data
      });
      window.location.href = '/login';
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to join organization');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'account':
        return (
          <Form onSubmit={accountForm.handleSubmit(onAccountSubmit)}>
            <h3 className="text-center mb-4">Create Your Account</h3>
            
            <Form.Group className="mb-3">
              <Form.Label>Your Name</Form.Label>
              <Form.Control
                type="text"
                {...accountForm.register('name')}
                isInvalid={!!accountForm.formState.errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {accountForm.formState.errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                {...accountForm.register('email')}
                isInvalid={!!accountForm.formState.errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {accountForm.formState.errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  {...accountForm.register('password')}
                  isInvalid={!!accountForm.formState.errors.password}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {accountForm.formState.errors.password?.message}
                </Form.Control.Feedback>
              </InputGroup>
              <div className="mt-2">
                <ProgressBar 
                  now={passwordStrength.score} 
                  variant={passwordStrength.variant as 'success' | 'warning' | 'danger' | 'info'}
                  label={passwordStrength.label}
                  className="mb-1"
                />
                <Form.Text className="text-muted">
                  Must be at least 8 characters and include uppercase, lowercase, number, and special character.
                </Form.Text>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  {...accountForm.register('confirmPassword')}
                  isInvalid={!!accountForm.formState.errors.confirmPassword}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  type="button"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {accountForm.formState.errors.confirmPassword?.message}
                </Form.Control.Feedback>
              </InputGroup>
              <div className="mt-2 d-flex align-items-center">
                {accountForm.watch('confirmPassword') && (
                  <>
                    {passwordMatch.matches ? (
                      <FaCheck className="text-success me-2" />
                    ) : (
                      <FaTimes className="text-danger me-2" />
                    )}
                    <Form.Text className={passwordMatch.matches ? "text-success" : "text-danger"}>
                      {passwordMatch.message}
                    </Form.Text>
                  </>
                )}
              </div>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                onClick={() => window.location.href = '/login'}
                className="flex-grow-1"
              >
                Back to Login
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                className="flex-grow-1"
                disabled={isLoading || !isAccountFormValid}
              >
                {isLoading ? 'Creating...' : 'Next'}
              </Button>
            </div>
          </Form>
        );

      case 'org-choice':
        return (
          <div className="text-center">
            <h3 className="mb-4">Organization Setup</h3>
            <p className="mb-4">Do you have an organization code or would you like to create a new organization?</p>
            
            <div className="d-grid gap-3">
              <Button 
                variant="primary" 
                onClick={() => setCurrentStep('create-org')}
                className="mb-2"
              >
                Create New Organization
              </Button>
              <Button 
                variant="outline-primary" 
                onClick={() => setCurrentStep('join-org')}
              >
                Join Existing Organization
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={() => setCurrentStep('account')}
              >
                Back
              </Button>
            </div>
          </div>
        );

      case 'create-org':
        return (
          <Form onSubmit={createOrgForm.handleSubmit(onCreateOrgSubmit)}>
            <h3 className="text-center mb-4">Create Organization</h3>
            
            <Form.Group className="mb-3">
              <Form.Label>Organization Name</Form.Label>
              <Form.Control
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
              />
              <Form.Text className="text-muted">
                This is the display name for your organization.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Organization ID</Form.Label>
              <div className="p-2 border rounded">
                {organizationName ? generateOrgId(organizationName) : 'Enter organization name to generate ID'}
              </div>
              <Form.Text className="text-muted">
                This will be used in your organization&apos;s URL.
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                onClick={() => setCurrentStep('org-choice')}
                className="flex-grow-1"
              >
                Back
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                className="flex-grow-1"
                disabled={isLoading || !organizationName}
              >
                {isLoading ? 'Creating...' : 'Create Organization'}
              </Button>
            </div>
          </Form>
        );

      case 'join-org':
        return (
          <Form onSubmit={joinOrgForm.handleSubmit(onJoinOrgSubmit)}>
            <h3 className="text-center mb-4">Join Organization</h3>
            
            <Form.Group className="mb-3">
              <Form.Label>Organization ID</Form.Label>
              <Form.Control
                type="text"
                {...joinOrgForm.register('orgId')}
                isInvalid={!!joinOrgForm.formState.errors.orgId}
                placeholder="e.g., acme_corp"
              />
              <Form.Text className="text-muted">
                Enter the organization&apos;s unique identifier.
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {joinOrgForm.formState.errors.orgId?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Organization Code</Form.Label>
              <Form.Control
                type="text"
                {...joinOrgForm.register('orgCode')}
                isInvalid={!!joinOrgForm.formState.errors.orgCode}
                placeholder="e.g., ABC123"
              />
              <Form.Text className="text-muted">
                Enter the 6-character code provided by your organization.
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {joinOrgForm.formState.errors.orgCode?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                onClick={() => setCurrentStep('org-choice')}
                className="flex-grow-1"
              >
                Back
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                className="flex-grow-1"
                disabled={isLoading}
              >
                {isLoading ? 'Joining...' : 'Join Organization'}
              </Button>
            </div>
          </Form>
        );
    }
  };

  return (
    <Container className="py-5">
      <Card className="mx-auto" style={{ maxWidth: '500px' }}>
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          {renderStep()}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AccountCreationForm; 